import { useCallback, useEffect, useState } from 'react';
import { FetchClipProps } from '../context/audio/AudioContext';
import useAudioElement from '../context/audio/useAudioElement';
import { mmSSFormat } from '../util/stringFormatting';
import audioManager from './audio/StudioAudioManager';

export type UseAudioPlayerState =
  | 'LOADING'
  | 'ERROR'
  | 'READY'
  | 'PLAYING'
  | 'PAUSED';

type UseAudioPlayerProps = Omit<FetchClipProps, 'src'> & {
  src?: string;
  setAudioPlaying?: (isPlaying: boolean) => void;
  /**
   * id - optional unique id
   * Added to allow maintaining multiple audio elements with the same src url
   */
  id?: string;
  shouldDisposeOnUnmount?: boolean;
};

export type UseAudioPlayerData = [
  {
    state: UseAudioPlayerState;
    errorMessage?: string;
    progress: number;
    formattedPlaybackPosition: string;
    seek: (progress: number) => void;
    togglePlayback: (options?: { shouldResetIfPaused: boolean }) => void;
  },
  HTMLAudioElement | undefined
];

const getAudioProgress = (audio: HTMLAudioElement) => {
  let audioProgress = audio.currentTime / audio.duration;
  if (Number.isNaN(audioProgress)) {
    audioProgress = 0;
  }
  return audioProgress;
};

const getFormattedPlaybackPosition = (audio: HTMLAudioElement) => {
  if (Number.isFinite(audio.currentTime) && Number.isFinite(audio.duration))
    return `${mmSSFormat(audio.currentTime)} / ${mmSSFormat(audio.duration)}`;
  return `loading...`;
};

const getStateFromAudioElem = (
  audio: HTMLAudioElement
): UseAudioPlayerState => {
  if (audio.paused) return 'PAUSED';
  if (audio.currentTime > 0) return 'PLAYING';
  if (audio.error) return 'ERROR';
  return 'READY';
};

/**
 * A wrapper around the `HTMLAudioElement` that provides useful state and lifecycle management. This
 * hook exposes the underlying audio element for full control.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLAudioElement
 *
 * @param {string | undefined} src Url of the target audio. If undefined, the audio element will
 *        remain in a LOADING state until a src is set.
 *
 * @todo It would make more sense for this hook to accept the HTMLAudioElement as a prop instead of
 * using that hook within this. For example, if `useAudioElement` does not resolve or errors, there
 * is no reason the setup the audio player portion.
 */
function useAudioPlayer({
  src,
  headers,
  setAudioPlaying,
  id,
  shouldDisposeOnUnmount,
}: UseAudioPlayerProps): UseAudioPlayerData {
  const [audio, audioError, setGlobalAudioElement] = useAudioElement({
    src: src || '',
    id,
    headers,
    shouldDisposeOnUnmount,
  });
  const [state, setState] = useState<UseAudioPlayerState>(
    audio ? getStateFromAudioElem(audio) : 'LOADING'
  );
  const [error, setError] = useState<string>(); // user friendly error message
  const [progress, setProgress] = useState<number>(
    audio ? getAudioProgress(audio) : 0
  ); // 0 - 1
  const [formattedPlaybackPosition, setFormattedPlaybackPosition] =
    useState<string>(
      audio ? getFormattedPlaybackPosition(audio) : `loading...`
    );

  /**
   * Event listeners
   */
  const onReady = useCallback(() => {
    // Check state to prevent future events from rolling back state
    if (audio) {
      setState(getStateFromAudioElem(audio));
      setFormattedPlaybackPosition(getFormattedPlaybackPosition(audio));
      // Browser inconsistencies, only respond to the first of these
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('loadend', onReady);
      audio.removeEventListener('loadedmetadata', onReady);
    }
  }, [audio]);
  const onError = useCallback(() => {
    if (!audio) return;
    setState('ERROR');
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
    const errorCode = audio.error ? audio.error.code : -1;
    switch (errorCode) {
      case MediaError.MEDIA_ERR_ABORTED:
        setError('Loading of the audio file was interrupted');
        break;
      case MediaError.MEDIA_ERR_NETWORK:
        setError('Unable to load audio due to network connection error');
        break;
      case MediaError.MEDIA_ERR_DECODE:
        setError('An error occurred while decoding the audio file');
        break;
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        setError('Unable to load audio file due to invalid src');
        break;
      default:
        setError('Failed to load audio file');
    }
  }, [audio]);
  const onDurationChange = useCallback(() => {
    if (!audio) return;
    // CASE `audioElement.duration === 0.0` The file has no content.
    // CASE `audioElement.duration === Infinity` Rare bug with an infinite duration (or streamed
    //      audio with no determinate length).
    // NOTE: `NaN` duration is legitimate and part of the `HTMLMediaElement` specification.
    if (audio.duration === 0.0 || audio.duration === Infinity) {
      setState('ERROR');
      setError('Unable to load audio file');
    }
  }, [audio]);
  const onProgressUpdate = useCallback(() => {
    if (!audio) return;
    const audioProgress = getAudioProgress(audio);
    // audio has finished playing
    if (setAudioPlaying && audioProgress === 1) {
      setAudioPlaying(false);
    }
    setProgress(audioProgress);
    setFormattedPlaybackPosition(getFormattedPlaybackPosition(audio));
  }, [audio, setAudioPlaying]);
  const onPlay = useCallback(() => {
    if (setAudioPlaying) {
      setAudioPlaying(true);
    }
    setState('PLAYING');
  }, [setAudioPlaying]);
  const onPause = useCallback(() => {
    if (setAudioPlaying) {
      setAudioPlaying(false);
    }
    setState('PAUSED');
  }, [setAudioPlaying]);

  /**
   * Event listener bindings
   */
  const addEventListeners = useCallback(
    (audioElem: HTMLAudioElement) => {
      audioElem.addEventListener('canplay', onReady);
      audioElem.addEventListener('loadend', onReady);
      audioElem.addEventListener('loadedmetadata', onReady);
      audioElem.addEventListener('durationchange', onDurationChange);
      audioElem.addEventListener('seeked', onProgressUpdate);
      audioElem.addEventListener('timeupdate', onProgressUpdate);
      audioElem.addEventListener('error', onError);
      audioElem.addEventListener('play', onPlay);
      audioElem.addEventListener('pause', onPause);
    },
    [onReady, onDurationChange, onProgressUpdate, onError, onPlay, onPause]
  );

  const removeEventListeners = useCallback(
    (audioElem: HTMLAudioElement) => {
      audioElem.removeEventListener('canplay', onReady);
      audioElem.removeEventListener('loadend', onReady);
      audioElem.removeEventListener('loadedmetadata', onReady);
      audioElem.removeEventListener('durationchange', onDurationChange);
      audioElem.removeEventListener('seeked', onProgressUpdate);
      audioElem.removeEventListener('timeupdate', onProgressUpdate);
      audioElem.removeEventListener('error', onError);
      audioElem.removeEventListener('play', onPlay);
      audioElem.removeEventListener('pause', onPause);
    },
    [onReady, onDurationChange, onProgressUpdate, onError, onPlay, onPause]
  );

  /**
   * Hook setup (fires when src changes or on initial mount)
   */
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (audio) {
      addEventListeners(audio);
      // NOTE: the audio element may _already_ be ready from a previous render
      if (Number.isFinite(audio.duration)) {
        setState(getStateFromAudioElem(audio));
        onProgressUpdate();
      }
      return () => {
        removeEventListeners(audio);
      };
    }
    // Important! Only trigger this effect on audio change!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio]);

  /**
   * If the underlying audio element
   */
  useEffect(() => {
    if (audioError) {
      setError(audioError.message);
      setState('ERROR');
    }
  }, [audioError]);

  /**
   * Seek helper method. Allow caller to seek to a percentage-based offset of the audio file. Used
   * in conjunction with the `progress` value.
   *
   * @param {number} position Float between 0 - 1 representing the percentage-based offset of the
   *                          audio file.
   */
  const seek = useCallback(
    (position: number) => {
      if (!audio) return;
      audio.currentTime = position * audio.duration;
    },
    [audio]
  );

  const togglePlayback = useCallback(
    (options?: { shouldResetIfPaused: boolean }) => {
      // Pause any audio managed by StudioAudioManager when preview starts
      audioManager.pause();
      if (!audio) {
        if (setAudioPlaying) {
          setAudioPlaying(false);
        }
        return;
      }
      if (state === 'PLAYING') {
        if (setAudioPlaying) {
          setAudioPlaying(false);
        }
        audio.pause();
      } else {
        const { shouldResetIfPaused } = options || {};
        if (setAudioPlaying) {
          setAudioPlaying(true);
        }
        if (src) {
          setGlobalAudioElement({
            src,
            shouldResetIfPaused: Boolean(shouldResetIfPaused),
            id,
          });
        }
        audio.play();
      }
    },
    [audio, setAudioPlaying, setGlobalAudioElement, src, state, id]
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return [
    {
      state,
      errorMessage: error,
      progress,
      formattedPlaybackPosition,
      seek,
      togglePlayback,
    },
    audio,
  ];
}

export default useAudioPlayer;
